import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import toast from '../../utils/toast';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';
import { normalizeUserRole, roleLabel } from '../../auth/roles';
import { useIqEntitlement } from '../hooks/use-iq-entitlement';
import { useDialogEscape } from '../hooks/use-dialog-focus';
import useBuildingData from '../hooks/use-get-building-data';
import { generateHandover, askLobbero } from '../../api/iq-client';
import {
  ShiftSummaryResponse,
  AskResponse,
} from '../../interfaces/iq-interfaces';
import { ModalPortal } from '../modal/modal-portal';
import {
  Presence,
  overlayMotion,
  preset,
  scrimMotion,
  useIsPhone,
} from '../../motion';
import { IqSummaryCard } from './iq-summary-card';
import { IqRawLog } from './iq-raw-log';
import { AuroraButton, GhostButton } from './iq-shared.styled';
import { IqAnswer } from './iq-answer';
import { IqOnDutyBlock } from './iq-on-duty-block';
import { IqHandoffInbox } from './iq-handoff-inbox';
import { useHandoffInbox } from './handoff-context';
import { FabBadge } from './iq-handoff.styled';
import { Fab } from './iq-dock.styled';
import { buildingWindowLabel } from '../../function-utils/building-time';

/* The launcher as a motion element, so it can fade against the panel (see the
   Presence at the bottom). motion.create() over the styled button: the styled
   layer still filters props, so nothing motion-only reaches the DOM. */
const MotionFab = motion.create(Fab);
import { useIqChat } from './iq-chat-context';
import {
  FloatPanel,
  FullOverlay,
  FullPanel,
  Header,
  HeaderTitle,
  HeaderActions,
  IconBtn,
  ContextBar,
  ScopeRow,
  ScopeChip,
  ScopeLabel,
  ScopeValue,
  Select,
  Thread,
  Welcome,
  WelcomeText,
  QBubble,
  ABubble,
  ThinkingDots,
  VisuallyHidden,
  CardTurn,
  Composer,
  Starters,
  Chip,
  InputRow,
  TextArea,
  Counter,
  Empty,
} from './iq-chat.styled';

const STARTERS = [
  'Any incidents this week?',
  'How many deliveries yesterday?',
  'Anyone denied entry?',
];
const MAX_BYTES = 400;
const byteLen = (s: string): number => new TextEncoder().encode(s).length;

// `WindowPreset` ('last' | 'custom') went with the shift control (WP-17).
// There is one window now, so there is nothing to choose between.

// Not a building id, and deliberately not a number: it is the one <option>
// value that means "do not send building_id at all".
const ORG_WIDE_OPTION = '__all__';

interface AskTurn {
  q: string;
  res: AskResponse | null;
  error?: string;
}

interface HandoverState {
  data: ShiftSummaryResponse | null;
  loading: boolean;
  error?: string;
}

// The shared label table, not a local capitalise. `users.role` is a snake_case
// capability key, so `role.charAt(0).toUpperCase()` produced "Property_manager"
// in the scope bar. A null role is somebody with no staff capability, and on
// this bar that is simply "You".
const scopeRoleLabel = (role?: string | null): string =>
  normalizeUserRole(role) ? roleLabel(role, { short: true }) : 'You';

// A datetime-local value carries no zone, so it is read as the operator's local
// time and converted to an absolute instant — which is exactly what the server's
// explicit-window path expects (new Date(from)).
// `toIso` (datetime-local -> ISO) went with the shift control's two date
// inputs (WP-17). Nothing in this file converts a typed local time any more.

/* A handover is a BUILDING record: its window is on the building's clock
   whoever reads it, and says so ("… · Building time — Sofia") — beside an
   answer that, for an owner or a manager, says "Your time — …". */
const fmtWindow = (from: string, to: string, tz: string): string =>
  buildingWindowLabel(from, to, tz);

export const IqChat: React.FC = () => {
  const { isOpen, mode, open, close, setMode, prefill, consumePrefill } = useIqChat();
  const { user, permissions, onboarding } = useAuth();
  const canUse = can(permissions, PERMISSIONS.IQ_USE);
  // The reader who can act on an account-level reason for IQ being off.
  const isIqAdmin = can(permissions, PERMISSIONS.IQ_ADMIN);
  const { state: entitlement } = useIqEntitlement({ enabled: canUse });
  // D-25 point 4. Read from the app-wide provider, NOT fetched here: this
  // component only loads once it is open, and a handover the reader has to open
  // the chat to discover is a handover they had to ask for.
  const {
    unreadCount,
    refresh: refreshHandoffs,
    setOverlayBuilding: setHandoffOverlayBuilding,
  } = useHandoffInbox();

  // Building context is owned HERE — the chat is global and mounts outside the
  // router that holds the app's selected building, so it resolves its own. One
  // building auto-selects; several show a picker. Only fetches once opened.
  const {
    selectedBuilding,
    buildingData: resolvedBuildings,
    selectBuildingById,
    loading: buildingsLoading,
  } = useBuildingData(user, { enabled: isOpen, loadAnalytics: false });
  // Defaulted, because it is now read ABOVE the early returns below rather than
  // only inside the JSX beneath them. The hook can hand back undefined before
  // its first resolution, and a component that throws while working out why a
  // control is disabled is a worse outcome than the disabled control.
  const buildingData = resolvedBuildings || [];

  const [handover, setHandover] = useState<HandoverState>({
    data: null,
    loading: false,
  });
  const [turns, setTurns] = useState<AskTurn[]>([]);
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [flashId, setFlashId] = useState<string | null>(null);

  // The shift window state that used to live here went with the control
  // (decision D7 / WP-17). The handover now always asks for the default window,
  // and a question's own period is parsed from the question itself.

  const abortRef = useRef<AbortController | null>(null);
  // Read by the scope-change effect below. Through a ref, not through the effect
  // deps: depending on `turns` there would re-run the reset on every answer.
  const turnsRef = useRef<AskTurn[]>(turns);
  turnsRef.current = turns;
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const buildingId = selectedBuilding ? Number(selectedBuilding.id) : NaN;
  const hasBuilding = Number.isInteger(buildingId);
  /* ── THE CHIP DECLARES THE DESK TO THE PROVIDER ──
     This panel resolves its OWN building (useBuildingData above, because the
     chat mounts outside the router that owns the app's selection) and, until
     now, never told the handover provider about it. So the provider answered
     the un-scoped question — "the newest handover anywhere I can see" — and this
     panel rendered that answer under whichever building its chip happened to
     show. Owner report, 2026-08-21: "i can't get the same handoff on 2 different
     buildings."
     NaN, not null, is what "nothing selected" is here, so the guard is
     Number.isInteger and the provider is told null in that case, which is the
     honest un-scoped question. */
  useEffect(() => {
    /* ONLY WHILE OPEN, and this is the correction that made the whole scoping
       change work. IqChat is mounted app-wide (App.tsx) and resolves its
       building with `{ enabled: isOpen }`, so while the panel is CLOSED it has
       no selection — and an unconditional declaration therefore published
       "no building" from every screen in the product, permanently. It won the
       race against the shift clock on the main menu and blanked the handover
       the gate had just been given.

       A panel that is not on screen is not asking a question, so it does not
       get to answer for the reader's scope. The surfaces that ARE showing a desk
       (the shift clock, the inbox inside this panel) declare it, and when this
       panel closes it releases the scope rather than pinning it to null. */
    if (!isOpen) {
      // RELEASED, not set to null. `undefined` hands the scope back to whatever
      // page is underneath — which on the main menu is the shift clock, showing
      // the desk the reader is actually at.
      setHandoffOverlayBuilding(undefined);

      return;
    }
    setHandoffOverlayBuilding(hasBuilding ? buildingId : null);
  }, [isOpen, hasBuilding, buildingId, setHandoffOverlayBuilding]);

  const entitled = !!entitlement?.enabled;
  const bytes = byteLen(q);
  const overCap = bytes > MAX_BYTES;

  /* ── WP-40 (e). THE SCOPE THE CODEBASE HAS BEEN WAITING FOR ──
     Owner ruling R-8: read the selected building; if the caller is unscoped,
     ASK which building — not guess, not silently span everything, not return
     nothing. The composer was hard-disabled with no explanation, which is the
     third of those.

     `askOrgWide` is DELIBERATE and never a default. Choosing "All buildings"
     from the picker is the reader answering the question, so nothing is spanned
     silently; and `POST /iq/ask` already treats `building_id` as optional
     (iq.js:1594-1601), letting RLS bound the answer rather than a JS branch. The
     answer side has been ready the whole time: res.scope.org_wide round-trips
     and iq-answer.tsx already badges it, and ask-facts.js already writes
     "Scope: your whole organisation — 4 buildings."
     iq-client.ts:113-125 records the decision to defer this control. */
  const [askOrgWide, setAskOrgWide] = useState(false);
  const scopeChosen = askOrgWide || hasBuilding;

  // ONE string: the test that disables the composer and the sentence shown
  // beside it. Two expressions is how a control ends up dead for a reason
  // nobody is told, which is exactly what this screen did.
  const composerBlockedReason = scopeChosen
    ? null
    : buildingData.length > 0
      ? 'Choose a building above — or All buildings — before asking.'
      : 'You do not have access to a building yet, so there is nothing for Lobbero IQ to read.';

  /* A different building is a different conversation — reset the thread so a
     handover for building A can never sit above questions about building B.

     WP-40 (c). THE RESET IS RIGHT; DOING IT IN SILENCE WAS NOT. `send()` clears
     the composer and pushes the question bubble BEFORE it awaits the answer, so
     a scope change mid-request destroyed the typed text, the bubble and the
     in-flight answer at once — no bubble, no answer, no error, which is exactly
     what was reported. Two changes: the question that was in flight is handed
     back to the composer so it can be re-asked, and the reset says it happened.

     It also no longer fires on the FIRST resolution of the scope. buildingId
     arrives as NaN and is filled in once the building list loads; wiping then
     cleared a prefill the opener had just seeded. */
  const scopeKey = askOrgWide ? 'org' : hasBuilding ? String(buildingId) : '';
  const lastScopeKey = useRef(scopeKey);
  useEffect(() => {
    if (lastScopeKey.current === scopeKey) return;
    const hadThread = turnsRef.current.length > 0;
    const unanswered = turnsRef.current.find(t => t.res == null && !t.error);
    lastScopeKey.current = scopeKey;

    setHandover({ data: null, loading: false });
    setTurns([]);
    abortRef.current?.abort();
    setAsking(false);

    // Give the unanswered question back rather than swallowing it. Only when
    // the composer is empty: a reader who has already started typing something
    // else must not have it overwritten.
    if (unanswered) {
      setQ(current => (current.trim() ? current : unanswered.q));
    }
    if (hadThread) {
      toast('Scope changed — earlier questions cleared.');
    }
    // eslint-disable-next-line
  }, [scopeKey]);

  /* A QUESTION COMPOSED BY THE OPENER, SEEDED INTO THE COMPOSER.
     `POST /iq/ask` takes `{building_id?, question}` and nothing else — there
     is no context parameter — so the Dashboard's "Ask IQ about this view"
     has to put the filter and the figures INSIDE the question. That is also what
     keeps the answer honest: it can only cite numbers that were on the reader's
     screen.

     IT IS NOT SENT AUTOMATICALLY. The composer is filled and left for the reader
     to edit or send. An auto-sent question would spend the organisation's Gemini
     allowance on a click that was, as far as the reader knows, "open the chat" —
     and this product meters that allowance.

     CONSUMED ON ARRIVAL, so re-opening the panel later does not resurrect a
     question about a filter that has since changed. */
  /* WP-40 (b). HELD, NOT CONSUMED, WHILE THE COMPOSER CANNOT TAKE IT.
     This used to seed and consume unconditionally. With no scope chosen the
     question landed in a disabled textarea that could not be edited, Ask stayed
     disabled so it could not be sent, and `consumePrefill()` immediately threw
     away the only other copy (iq-chat-context.tsx:58). The composed question was
     unrecoverable and nothing said so. The prefill now waits in the context
     until there is somewhere for it to go. */
  useEffect(() => {
    if (!isOpen || !prefill || !scopeChosen) return;
    setQ(prefill);
    consumePrefill();
  }, [isOpen, prefill, consumePrefill, scopeChosen]);

  // Keep the newest turn in view; the handover card stays pinned at the top.
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [turns, asking]);

  // Escape closes, on the SAME stack every other dialog uses. This panel is
  // mounted app-wide (App.tsx:66), so it can be open at the same time as a
  // dashboard modal; its own listener closed both on one key. The hook keeps the
  // focus and scroll handling here (this panel deliberately does not trap focus
  // or lock body scroll) and only shares the ordering.
  // locksScroll: false — this panel sits OVER a page the reader is still meant
  // to read, and freezing it was the opposite of what the comment above claims.
  // It stays on the stack for Escape ordering only.
  useDialogEscape(isOpen, close, { locksScroll: false });

  // The full-screen shell grows from the centre on a desk and rises as a
  // takeover on a phone, where it is the whole screen.
  const phone = useIsPhone();

  /**
   * FOCUS GOES IN, AND COMES BACK OUT.
   *
   * The launcher is the focused element when it is pressed, and
   * `if (!isOpen) return <Fab/>` unmounts it the instant the panel opens — so
   * focus fell to <body>, and nothing inside the panel ever received it. A
   * keyboard user opened IQ and lost their place entirely; closing it left them
   * at the top of the document rather than back on the button they pressed.
   *
   * The panel deliberately does not TRAP focus — it is a corner panel over a
   * page that stays usable — so this moves focus in and restores it, and no
   * more.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      // After paint: the panel is being mounted in this same commit.
      const raf = window.requestAnimationFrame(() => {
        panelRef.current?.focus();
      });

      return () => window.cancelAnimationFrame(raf);
    }

    const previous = returnFocusRef.current;

    returnFocusRef.current = null;
    if (previous && document.contains(previous)) {
      previous.focus();
    }

    return undefined;
  }, [isOpen]);

  // Every building this reader can see, not only the selected one. A handover is
  // addressed to a PERSON, so somebody covering two desks receives records from
  // both and each must be labelled with its OWN building.
  const resolveBuildingName = useCallback(
    (id: number): string | undefined =>
      buildingData.find(b => Number(b.id) === id)?.property_name,
    [buildingData]
  );

  // One window, so one caption. The two other branches described states that
  // can no longer be reached now the picker is gone, and a caption offering to
  // "pick a start and end" for a control that does not exist is worse than no
  // caption at all.
  const windowHint = 'The last completed shift for this building.';

  const runHandover = async (force: boolean): Promise<void> => {
    if (!hasBuilding || !entitled) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setHandover(h => ({ ...h, loading: true, error: undefined }));
    try {
      // No explicit window: the backend resolves its default, which is the last
      // completed shift for this building. That is what this control was set to
      // for everyone who never opened it.
      const res = await generateHandover(
        { building_id: buildingId, force },
        { signal: ac.signal }
      );
      setHandover({ data: res, loading: false });
    } catch (e) {
      if (ac.signal.aborted) return;
      const message =
        e instanceof Error ? e.message : 'Could not reach Lobbero IQ.';
      setHandover({ data: null, loading: false, error: message });
      toast.error(message);
    }
  };

  const send = async (text: string): Promise<void> => {
    const question = text.trim();
    if (!question || byteLen(question) > MAX_BYTES || asking || !scopeChosen)
      return;
    setQ('');
    setAsking(true);
    setTurns(t => [...t, { q: question, res: null }]);
    try {
      // building_id OMITTED for the org-wide question — the route treats it as
      // optional and lets RLS decide the reach, which is narrower and more
      // trustworthy than any id this client could compute.
      const res = await askLobbero({
        building_id: askOrgWide ? undefined : buildingId,
        question,
      });
      setTurns(t =>
        t.map((turn, i) => (i === t.length - 1 ? { ...turn, res } : turn))
      );
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : 'Could not reach Lobbero IQ.';
      setTurns(t =>
        t.map((turn, i) =>
          i === t.length - 1 ? { ...turn, error: msg } : turn
        )
      );
    } finally {
      setAsking(false);
    }
  };

  const scrollToSource = (id: string): void => {
    const el = document.getElementById(`iq-entry-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashId(id);
    window.setTimeout(() => setFlashId(null), 1200);
  };

  // Suppress entirely for anyone who cannot use IQ — the aurora mark is reserved.
  //
  // ── THE ENTITLEMENT DOES NOT HIDE IT FROM AN ADMIN ──
  //
  // Owner instruction: the launcher has to exist for the admin on every screen.
  // It used to disappear the moment `enabled` came back false, and the most
  // common reason for that is `tier_unattested` — an account state the ADMIN is
  // the only person who can do anything about. Hiding the entry point from the
  // one reader who can fix it, with no way to find out it is off, is the worst
  // possible place to fail closed.
  //
  // Nothing is unlocked by showing it: `canGenerate` below is still gated on the
  // entitlement, so the surface opens, the handovers and the raw log are there —
  // both of which are ordinary shift data and never touched the model — and the
  // generate button is disabled with the reason printed beside it. Everyone
  // without `iq:admin` keeps the old behaviour exactly.
  if (!user) return null;
  if (!onboarding?.completed) return null;
  if (!canUse) return null;
  if (!entitled && !isIqAdmin) return null;

  /* THE LAUNCHER AND THE PANEL ARE ONE PRESENCE. They are mutually exclusive
     mounts, so putting both under the same Presence is what lets the FAB fade
     out while the panel rises, and the panel fall away while the FAB fades
     back - instead of one vanishing the frame the other appears. */
  const fab = (
      <MotionFab
        key="iq-fab"
        variants={preset.state}
        initial="hidden"
        animate="visible"
        exit="hidden"
        type="button"
        // THE NAME THE REST OF THE PRODUCT ALREADY USES FOR THIS BUTTON.
        // print.styled.tsx hides [data-testid='iq-launcher'] and the analytics
        // E2E scrapes it — both were written against iq-dock.tsx, which nothing
        // has mounted since this component replaced it, so the print rule hid
        // nothing and the E2E measured a selector that never matched.
        data-testid="iq-launcher"
        // The count is IN THE LABEL, not only in the badge: a screen reader gets
        // the same fact the badge shows, and design_system §7 forbids conveying
        // state through colour alone.
        aria-label={
          unreadCount > 0
            ? `Open Lobbero IQ — ${unreadCount} unread shift handover${
                unreadCount === 1 ? '' : 's'
              }`
            : 'Open Lobbero IQ'
        }
        title={
          unreadCount > 0
            ? `Lobbero IQ — ${unreadCount} unread handover${
                unreadCount === 1 ? '' : 's'
              }`
            : 'Lobbero IQ'
        }
        onClick={() => {
          refreshHandoffs();
          open('float');
        }}
      >
        ✦
        {unreadCount > 0 ? (
          <FabBadge aria-hidden>{unreadCount > 9 ? '9+' : unreadCount}</FabBadge>
        ) : null}
      </MotionFab>
  );

  const buildingName = selectedBuilding?.property_name || '—';
  const portfolioName = selectedBuilding?.portfolio_name || '—';
  const windowLabel = handover.data
    ? fmtWindow(
        handover.data.window.from,
        handover.data.window.to,
        handover.data.window.tz
      )
    : windowHint;

  const detail = handover.data?.degraded?.detail;
  const detailTotal =
    detail && typeof detail === 'object' ? detail.total : undefined;
  const totalEntries =
    detailTotal ??
    handover.data?.total ??
    (handover.data ? handover.data.entries.length : 0);

  const inner = (
    <>
      <Header>
        <HeaderTitle>
          <span aria-hidden>✦</span>
          Lobbero IQ
        </HeaderTitle>
        <HeaderActions>
          <IconBtn
            type="button"
            aria-label={
              mode === 'full' ? 'Collapse to corner' : 'Expand to full screen'
            }
            title={mode === 'full' ? 'Collapse' : 'Full screen'}
            onClick={() => setMode(mode === 'full' ? 'float' : 'full')}
          >
            {mode === 'full' ? '⤡' : '⤢'}
          </IconBtn>
          <IconBtn
            type="button"
            aria-label="Close Lobbero IQ"
            title="Close"
            onClick={close}
          >
            ✕
          </IconBtn>
        </HeaderActions>
      </Header>

      <ContextBar>
        <ScopeRow>
          <ScopeChip>
            <ScopeLabel>Portfolio</ScopeLabel>
            <ScopeValue title={portfolioName}>{portfolioName}</ScopeValue>
          </ScopeChip>
          <ScopeChip>
            <ScopeLabel>Building</ScopeLabel>
            {/* WP-40 (e). RENDERED WHENEVER THERE IS ANYTHING TO CHOOSE, not
                only above one building. The old `length > 1` test meant a reader
                with exactly one building had no control at all — and the
                disabled-composer message still told them to "select a building
                above", pointing at nothing.

                ALL BUILDINGS is an explicit option, never a default. R-8 forbids
                silently spanning the organisation; it does not forbid the reader
                asking for it. Selecting it omits building_id from the request and
                lets RLS bound the answer. */}
            {buildingData.length > 0 ? (
              <Select
                aria-label="Building"
                value={askOrgWide ? ORG_WIDE_OPTION : selectedBuilding?.id || ''}
                onChange={e => {
                  if (e.target.value === ORG_WIDE_OPTION) {
                    setAskOrgWide(true);

                    return;
                  }
                  setAskOrgWide(false);
                  selectBuildingById(e.target.value);
                }}
              >
                <option value="" disabled>
                  Select…
                </option>
                {buildingData.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.property_name}
                  </option>
                ))}
                <option value={ORG_WIDE_OPTION}>
                  All buildings I can see
                </option>
              </Select>
            ) : (
              <ScopeValue title={buildingName}>{buildingName}</ScopeValue>
            )}
          </ScopeChip>
          <ScopeChip>
            <ScopeLabel>User</ScopeLabel>
            <ScopeValue title={user.full_name}>
              {user.full_name} · {scopeRoleLabel(user.role)}
            </ScopeValue>
          </ScopeChip>
        </ScopeRow>
        {/* THE SHIFT CONTROL IS GONE (decision D7, 2026-08-20).
            The scope bar is a bar of PLACES and PEOPLE — portfolio, building,
            user. Shift was the one control on it that set a TIME WINDOW, and a
            question already carries its own time: "what happened in the last 2
            days", "yesterday", "on the night shift". Two mechanisms setting the
            same window is how they came to disagree.

            THE CAPABILITY IS NOT LOST, WHICH IS THE CONDITION THE OWNER SET.
            This control's "Custom range…" was the only way to ask about a window
            other than your own last completed shift, so deleting it alone would
            have quietly removed a capability. The replacement is that a period
            named in the question is parsed and used (parseQuestionRange in
            src/iq/ask-semantic.js), and when retrieval finds nothing inside a
            named window the backend now reads the window directly instead of
            answering "0 entries" (WP-15).

            The handover keeps working: with no explicit window it uses the
            default, which is the last completed shift — the same thing this
            control was set to for everyone who never touched it. */}
      </ContextBar>

      {/* THE ANSWER IS SPOKEN, NOT ONLY DRAWN.
          Answers, errors and the "Checking the log…" placeholder were all
          injected into this container with no live region, so a screen-reader
          user pressed Ask and was told nothing at all — on a surface whose
          entire output is text that arrives later.
          `polite` rather than `assertive`: the reader asked for this, so it
          waits for a gap rather than interrupting them mid-sentence. */}
      <Thread aria-live="polite" aria-busy={asking || undefined}>
        <Welcome>
          <WelcomeText>
            I read this building&apos;s log and brief you on the shift. Generate
            the handover, or ask a question below.
          </WelcomeText>
        </Welcome>

        {/* ══════════════════════════ D-25 POINT 4 ══════════════════════════
            PINNED AT THE TOP OF THE THREAD, ABOVE THE ON-DEMAND SUMMARY, AND
            NOT BEHIND A BUTTON. "That user's IQ should have the summary when he
            logs in" — so the first thing in the conversation is the shift that
            was handed to them, already open, before anybody asks anything.

            IT IS OUTSIDE THE BUILDING BRANCH BELOW so that it renders while the
            building list is still loading — but it is SCOPED TO THE SELECTED
            DESK, which is a correction to what this comment used to say.

            It said "a handover is addressed to a PERSON, not to the building this
            panel happens to have selected". That was true of the pre-076 model
            and is not true now: since the owner's 2026-08-11 reversal a handover
            is addressed to the DESK (recipient_user_id NULL until somebody clocks
            in and claims it), and both the client and the server already say so
            — handoff-client's getLatestHandoff and shift-handoff.js's latest
            route are both documented ONE DESK AT A TIME. Three statements, one
            of them stale, is how the same handover came to be rendered under two
            different buildings: owner report, 2026-08-21, "i can't get the same
            handoff on 2 different buildings."

            So the panel declares its chip to the provider (see the effect above)
            and the inbox reads through `latestFor`, which refuses a row that is
            not about this desk. The reader who covers two desks is not cut off —
            they switch the chip, which is the control that says which desk they
            are asking about. */}
        <CardTurn>
          <IqHandoffInbox
            currentUserId={user.id}
            resolveBuildingName={resolveBuildingName}
            buildingId={buildingId}
          />
        </CardTurn>

        {buildingsLoading && !hasBuilding ? (
          <Empty>Loading your buildings…</Empty>
        ) : !hasBuilding ? (
          <Empty>Select a building above to begin.</Empty>
        ) : (
          <CardTurn>
            {handover.error && !handover.data ? (
              <ABubble>
                {handover.error}
                <div>
                  <GhostButton type="button" onClick={() => runHandover(false)}>
                    Try again
                  </GhostButton>
                </div>
              </ABubble>
            ) : (
              <IqSummaryCard
                data={handover.data}
                loading={handover.loading}
                windowLabel={windowLabel}
                entryCount={handover.data ? handover.data.entries.length : null}
                canGenerate={hasBuilding && entitled}
                hasBuilding={hasBuilding}
                onGenerate={() => runHandover(false)}
                onForce={() => runHandover(true)}
                onRetry={() => runHandover(false)}
                onScrollToSource={scrollToSource}
              />
            )}
            {/* Between the AI card and the raw log, because it answers the
                first question a reader has about the summary above ("who am I
                taking over from?") and the last one they have about the log
                below ("who wrote this?"). The key is ABSENT for a caller
                without user:read — a concierge simply never receives it — so
                the presence check IS the permission check. */}
            {handover.data?.on_duty ? (
              <IqOnDutyBlock
                data={handover.data.on_duty}
                tz={handover.data.window.tz}
              />
            ) : null}
            {handover.data && handover.data.entries.length ? (
              <IqRawLog
                entries={handover.data.entries}
                tz={handover.data.window.tz}
                truncated={handover.data.truncated}
                entryLimit={
                  handover.data.window.entry_limit ||
                  handover.data.entries.length
                }
                total={totalEntries}
                flashId={flashId}
              />
            ) : null}
          </CardTurn>
        )}

        {turns.map((turn, i) => (
          <React.Fragment key={i}>
            <QBubble>{turn.q}</QBubble>
            {turn.res ? (
              // Shared with IqAskPanel — see iq-answer.tsx for why there is now
              // exactly one copy of this.
              <ABubble $wide={turn.res.mode === 'list' || turn.res.mode === 'facts'}>
                <IqAnswer res={turn.res} />
              </ABubble>
            ) : turn.error ? (
              <ABubble>{turn.error}</ABubble>
            ) : (
              <ABubble>
                <ThinkingDots aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </ThinkingDots>
                <VisuallyHidden>Checking the log…</VisuallyHidden>
              </ABubble>
            )}
          </React.Fragment>
        ))}
        <div ref={threadEndRef} />
      </Thread>

      <Composer>
        <Starters>
          {STARTERS.map(s => (
            <Chip
              key={s}
              type="button"
              disabled={asking || !scopeChosen}
              onClick={() => send(s)}
            >
              {s}
            </Chip>
          ))}
        </Starters>
        <InputRow>
          <TextArea
            value={q}
            placeholder={
              askOrgWide
                ? 'Ask about every building you can see…'
                : "Ask about this building's log…"
            }
            disabled={!scopeChosen}
            aria-describedby={
              composerBlockedReason ? 'iq-composer-reason' : undefined
            }
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(q);
              }
            }}
          />
          <AuroraButton
            type="button"
            disabled={asking || !q.trim() || overCap || !scopeChosen}
            onClick={() => send(q)}
          >
            Ask
          </AuroraButton>
        </InputRow>
        {/* A DISABLED COMPOSER WITH NO REASON IS A BROKEN COMPOSER. The byte
            counter is meaningless while nothing can be typed, so the reason
            takes its place rather than sitting beside it. */}
        <Counter $over={overCap} id="iq-composer-reason">
          {composerBlockedReason || (
            <>
              {bytes}/{MAX_BYTES}
              {overCap ? ' · too long' : ''}
            </>
          )}
        </Counter>
      </Composer>
    </>
  );

  return (
    <Presence>
      {!isOpen ? (
        fab
      ) : (
    <ModalPortal key="iq-panel">
      {mode === 'full' ? (
        <FullOverlay
          {...scrimMotion}
          onMouseDown={e => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <FullPanel
            {...overlayMotion(phone ? 'takeover' : 'modal')}
            ref={panelRef}
            role="dialog"
            aria-label="Lobbero IQ"
            /* -1: focusable by script, never by Tab. The panel itself is the
               landing point when it opens; Tab then walks its controls. */
            tabIndex={-1}
          >
            {inner}
          </FullPanel>
        </FullOverlay>
      ) : (
        <FloatPanel
          {...overlayMotion('panel')}
          ref={panelRef}
          role="dialog"
          aria-label="Lobbero IQ"
          tabIndex={-1}
        >
          {inner}
        </FloatPanel>
      )}
    </ModalPortal>
      )}
    </Presence>
  );
};
